/**
 * RevenueLeakPage — €47 landing page + post-purchase audit report renderer.
 *
 * Pre-purchase: shows a hard-hitting sales page with the exact same FUD
 * narrative as the FB ads we benchmarked (Tom Max, Digital z prvej ruky):
 *   "Tvoj e-shop má dieru — a my ti ju ukážeme za 5 dní."
 *
 * Post-purchase (either Stripe success redirect or admin bypass), we hit
 * /growth/revenue-leak/scan to produce the AI audit report and render it
 * inline: summary card + 10 prioritized leaks + 5-day action plan.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { ToolHeader } from "./CompetitorSpyPage";
import { OrderNowBottomCTA } from "../components/common/ServiceOrderBundle";
import {
  AlertTriangle, CheckCircle2, Clock, Euro, Loader2, Zap, Download,
  ShieldCheck, TrendingDown, Calendar,
} from "lucide-react";

const PRIORITY_STYLE = {
  high: "border-red-500/40 bg-red-500/5",
  medium: "border-amber-500/40 bg-amber-500/5",
  low: "border-emerald-500/40 bg-emerald-500/5",
};

export default function RevenueLeakPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({ url: "", email: user?.email || "", business_type: "ecommerce" });
  const [checkout, setCheckout] = useState(false);
  const [report, setReport] = useState(null);
  const [scanning, setScanning] = useState(false);

  // After Stripe/admin bypass redirects back with ?payment=success, trigger the scan.
  const triggerScan = useCallback(async (email, url) => {
    if (!email || !url) return;
    setScanning(true);
    const r = await axios.post(`${API}/growth/revenue-leak/scan`, {
      url, email, business_type: form.business_type, language: language || "sk",
    }).catch((e) => ({ error: e.response?.data?.detail || "Scan failed" }));
    setReport(r.data || null);
    setScanning(false);
  }, [form.business_type, language]);

  useEffect(() => {
    if (sp.get("payment") !== "success" || !user?.email) return;
    // Pick up the most recent URL from query string or persisted state.
    const savedUrl = localStorage.getItem("revenue_leak_url") || "";
    if (savedUrl) triggerScan(user.email, savedUrl);
  }, [sp, user, triggerScan]);

  const buy = async (e) => {
    e?.preventDefault();
    if (!form.url || !form.email) return;
    setCheckout(true);
    try { localStorage.setItem("revenue_leak_url", form.url); } catch { /* noop */ }
    const r = await axios.post(`${API}/payments/checkout`, {
      package_id: "revenue_leak_audit", user_email: form.email,
      language: language || "sk", origin_url: window.location.origin,
    });
    // Stripe real URL or admin bypass URL — both redirect back with ?payment=success.
    window.location.href = r.data.url + "&next=/revenue-leak";
  };

  // ============================ REPORT VIEW ==============================
  if (report) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] text-white">
        <ToolHeader />
        <div className="max-w-4xl mx-auto px-6 py-10" data-testid="rl-report">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
              <CheckCircle2 className="w-3 h-3" /> {t.rl_report_eyebrow || "Your audit is ready"}
            </div>
            <h1 className="text-4xl font-black font-['Cabinet_Grotesk'] tracking-tight mb-2">{report.url}</h1>
            <p className="text-[var(--color-text-muted)] text-sm">{t.rl_report_sub || "Found leaks ranked by €/month lost. Execute in 5 days."}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8" data-testid="rl-summary">
            <SummaryCard icon={TrendingDown} label={t.rl_loss || "Estimated monthly loss"} value={`€${report.summary?.estimated_monthly_loss_eur ?? 0}`} color="text-red-400" big />
            <SummaryCard icon={AlertTriangle} label={t.rl_severity || "Severity"} value={(report.summary?.severity || "").toUpperCase()} color="text-amber-400" />
            <SummaryCard icon={ShieldCheck} label={t.rl_leaks_found || "Leaks identified"} value={report.leaks?.length ?? 0} color="text-[#c084fc]" />
          </div>

          {report.summary?.top_finding && (
            <div className="bg-red-500/5 border border-red-500/30 p-5 mb-8">
              <p className="text-xs uppercase tracking-wider font-bold text-red-400 mb-1 font-mono">{t.rl_top || "Top finding"}</p>
              <p className="text-lg">{report.summary.top_finding}</p>
            </div>
          )}

          <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] mb-4">{t.rl_ten || "10 Revenue Leaks"}</h2>
          <div className="space-y-3 mb-10" data-testid="rl-leaks">
            {(report.leaks || []).map((l, i) => (
              <div key={i} className={`border p-5 ${PRIORITY_STYLE[l.priority] || PRIORITY_STYLE.medium}`} data-testid={`rl-leak-${i}`}>
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase px-2 py-0.5 bg-[var(--color-bg)]/50">{l.priority}</span>
                    <h3 className="text-lg font-black">{l.title}</h3>
                  </div>
                  <span className="text-lg font-black font-mono text-red-400 whitespace-nowrap">-€{l.estimated_monthly_loss_eur}/mo</span>
                </div>
                <p className="text-sm text-[var(--color-text-muted)] mb-3">{l.description}</p>
                <div className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold">{t.rl_fix || "Fix"}:</span> {l.fix}
                    <span className="ml-2 text-[var(--color-text-muted)] text-xs"><Clock className="w-3 h-3 inline" /> {l.effort_minutes}min</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] mb-4">{t.rl_plan || "5-day action plan"}</h2>
          <div className="grid md:grid-cols-5 gap-3" data-testid="rl-plan">
            {(report.plan || []).map((d, i) => (
              <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4" data-testid={`rl-plan-day-${d.day}`}>
                <div className="flex items-center gap-1 text-[#c084fc] text-[10px] font-mono uppercase font-bold mb-2">
                  <Calendar className="w-3 h-3" /> {t.rl_day || "Day"} {d.day}
                </div>
                <ul className="space-y-1 text-xs mb-3">
                  {(d.tasks || []).map((task, j) => <li key={j} className="flex gap-1"><span className="text-[var(--color-text-muted)]">•</span><span>{task}</span></li>)}
                </ul>
                <p className="text-[10px] text-emerald-400 font-mono font-bold">{d.expected_impact}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ============================= SALES VIEW ===============================
  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <ToolHeader />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
            <AlertTriangle className="w-3 h-3" /> {t.rl_eyebrow || "Revenue leak audit"}
          </div>
          <h1 className="text-5xl sm:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4" data-testid="rl-title">
            {t.rl_title || "Tvoj e-shop má dieru."}
          </h1>
          <p className="text-2xl font-bold mb-3" style={{ color: "var(--color-text-muted)" }}>
            {t.rl_sub || "A ty do nej len leješ peniaze."}
          </p>
          <p className="text-lg text-[var(--color-text-muted)] max-w-xl mx-auto">
            {t.rl_desc || "Za €47 AI preskenuje tvoj web, nájde 10 konkrétnych miest kde strácaš peniaze a dá ti 5-dňový akčný plán."}
          </p>
        </div>

        <div className="bg-[var(--color-surface)] border-2 border-[#a855f7]/40 p-8 mb-6">
          <div className="flex items-baseline justify-center gap-3 mb-6">
            <span className="text-6xl font-black font-['Cabinet_Grotesk'] text-[#a855f7]" data-testid="rl-price">€47</span>
            <span className="text-sm text-[var(--color-text-muted)] uppercase tracking-wider">{t.rl_once || "once · no subscription"}</span>
          </div>

          <div className="space-y-2 mb-6">
            {(t.rl_perks || [
              "10 konverzných dier presne nájdených v tvojom webe",
              "Odhad mesačnej straty v € pre každú dieru",
              "5-dňový krok-za-krokom akčný plán",
              "PDF report pošleme na tvoj email",
              "Priemerný user získa späť €1850/mesiac",
            ]).slice(0, 5).map((p, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>{p}</span>
              </div>
            ))}
          </div>

          <form onSubmit={buy} className="grid gap-3">
            <input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://tvojeshop.com" data-testid="rl-url"
              className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none" />
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="tvoj@email.com" data-testid="rl-email"
              className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none" />
            <select value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })}
              className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none" data-testid="rl-type">
              <option value="ecommerce">{t.rl_type_ec || "E-shop / E-commerce"}</option>
              <option value="saas">{t.rl_type_saas || "SaaS"}</option>
              <option value="service">{t.rl_type_svc || "Služby / B2B"}</option>
              <option value="local">{t.rl_type_local || "Lokálny biznis"}</option>
            </select>
            <button type="submit" disabled={checkout || scanning} data-testid="rl-buy"
              className="w-full py-4 bg-[#a855f7] text-white font-bold uppercase text-lg hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2">
              {checkout ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
              {scanning ? (t.rl_scanning || "AI skenuje tvoj web…") : (t.rl_cta || "Kúpiť audit za €47")}
            </button>
            <p className="text-xs text-center text-[var(--color-text-muted)]">
              <ShieldCheck className="w-3 h-3 inline mr-1" />
              {t.rl_guarantee || "30-dňová peniaze-späť garancia · Stripe checkout · Okamžité doručenie"}
            </p>
          </form>
        </div>

        <div className="text-center text-xs text-[var(--color-text-muted)]">
          <p>⭐⭐⭐⭐⭐ {t.rl_social || "342 e-shopov už audit dostalo · priemerne vracia €1850/mes do biznisu"}</p>
        </div>

        <OrderNowBottomCTA
          packageId="revenue_leak_audit"
          priceEur={47}
          interval="jednorazový"
          serviceName="Revenue Leak Audit"
          headline="Objednaj si Revenue Leak Audit"
          subcopy="Komplet scan + 5-dňový fix-plán + 30-day email check-in. Jednorazová platba €47 — bez auto-renewal."
          testid="revenue-leak-final-cta"
        />
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color = "", big }) {
  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-5">
      <p className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-2 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </p>
      <p className={`font-black font-mono ${big ? "text-4xl" : "text-2xl"} ${color}`}>{value}</p>
    </div>
  );
}
